import { configureStore, ThunkAction, Action } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import { combineReducers } from "redux";
import { get } from "lodash";

import storageSession from "redux-persist/lib/storage/session";
import loadingReducer from "./slices/loadingSlice";
import staticDataReducer from "./slices/staticSlice";
import urlAnalyticReducer from "./slices/MLTC_R3/urlAnalyticSlice";

import sioStep1Reducer from "./slices/SIO/step1Slice";
import sioStep2Reducer from "./slices/SIO/step2Slice";
import sioStep3Reducer from "./slices/SIO/step3Slice";
import sioStep4Reducer from "./slices/SIO/step4Slice";
import sioStep5Reducer from "./slices/SIO/step5Slice";

import dpiStep1Reducer from "./slices/DPI/step1Slice";
import dpiStep2Reducer from "./slices/DPI/step2Slice";
import dpiStep3Reducer from "./slices/DPI/step3Slice";
import dpiStep4Reducer from "./slices/DPI/step4Slice";
import dpiStep5Reducer from "./slices/DPI/step5Slice";

import mltcStep1Reducer from "./slices/MLTC/step1Slice";
import mltcStep2Reducer from "./slices/MLTC/step2Slice";
import mltcStep3Reducer from "./slices/MLTC/step3Slice";
import mltcStep4Reducer from "./slices/MLTC/step4Slice";
import mltcStep5Reducer from "./slices/MLTC/step5Slice";

/*MLTC R3*/
import mltcR3Step1Reducer from "./slices/MLTC_R3/step1Slice";
import mltcR3Step2Reducer from "./slices/MLTC_R3/step2Slice";
import mltcR3Step3Reducer from "./slices/MLTC_R3/step3Slice";
import mltcR3Step4Reducer from "./slices/MLTC_R3/step4Slice";
import mltcR3Step5Reducer from "./slices/MLTC_R3/step5Slice";
import mltcR3Step6Reducer from "./slices/MLTC_R3/step6Slice";
import mltcR3ContactReducer from "./slices/MLTC_R3/contactSlice";
import mltcR3PlanSheetReducer from "./slices/MLTC_R3/planBottomSheetSlice";

import shieldStarterStep1Reducer from "./slices/SHIELD_STARTER/step1Slice";
import shieldStarterStep2Reducer from "./slices/SHIELD_STARTER/step2Slice";
import shieldStarterStep3Reducer from "./slices/SHIELD_STARTER/step3Slice";
import shieldStarterStep4Reducer from "./slices/SHIELD_STARTER/step4Slice";
import shieldStarterStep5Reducer from "./slices/SHIELD_STARTER/step5Slice";

// Evergreen
import everGreenStep1Reducer from "./slices/EVER_GREEN/step1Slice";
import everGreenStep2Reducer from "./slices/EVER_GREEN/step2Slice";
import everGreenStep3Reducer from "./slices/EVER_GREEN/step3Slice";
import everGreenContactReducer from "./slices/EVER_GREEN/contactSlice";
import everGreenStep4Reducer from "./slices/EVER_GREEN/step4Slice";

//Cancer Cover
import cancerCoverStep1Reducer from "./slices/CCP/step1Slice";
import cancerCoverStep3Reducer from "./slices/CCP/step3Slice";
import cancerCoverStep2Reducer from "./slices/CCP/step2Slice";
import cancerCoverContact from "./slices/CCP/contactSlice";
import cancerCoverStep4Reducer from './slices/CCP/step4Slice'
import cancerCoverStep6Reducer from './slices/CCP/step6Slice'

import staticReduce from "@static/staticSlice";
import { Helpers } from "@utils/helpers";
import modalViewReducer from "./slices/modal";
import queryReducer from "./slices/querySlice";
// ...

const reducers = combineReducers({
  query: queryReducer,
  /**Modal View Reducer to manage modal state */
  modalView: modalViewReducer,
  /**Global for both SIO and DPI */
  loading: loadingReducer,
  staticData: staticDataReducer,

  static: staticReduce, // Need to be removed
  /**SIO */
  sioStep1: sioStep1Reducer,
  sioStep2: sioStep2Reducer,
  sioStep3: sioStep3Reducer,
  sioStep4: sioStep4Reducer,
  sioStep5: sioStep5Reducer,
  /**DPI */
  dpiStep1: dpiStep1Reducer,
  dpiStep2: dpiStep2Reducer,
  dpiStep3: dpiStep3Reducer,
  dpiStep4: dpiStep4Reducer,
  dpiStep5: dpiStep5Reducer,
  /**MLTC */
  mltcStep1: mltcStep1Reducer,
  mltcStep2: mltcStep2Reducer,
  mltcStep3: mltcStep3Reducer,
  mltcStep4: mltcStep4Reducer,
  mltcStep5: mltcStep5Reducer,

  /**MLTC R3 */
  mltcR3Step1: mltcR3Step1Reducer,
  mltcR3Step2: mltcR3Step2Reducer,
  mltcR3Step3: mltcR3Step3Reducer,
  mltcR3Step4: mltcR3Step4Reducer,
  mltcR3Step5: mltcR3Step5Reducer,
  mltcR3Step6: mltcR3Step6Reducer,
  mltcR3Contact: mltcR3ContactReducer,
  mltcR3PlanSheet: mltcR3PlanSheetReducer,
  urlAnalytic: urlAnalyticReducer,

  /** SHIELD STARTER */
  shieldStarterStep1: shieldStarterStep1Reducer,
  shieldStarterStep2: shieldStarterStep2Reducer,
  shieldStarterStep3: shieldStarterStep3Reducer,
  shieldStarterStep4: shieldStarterStep4Reducer,
  shieldStarterStep5: shieldStarterStep5Reducer,

  /** Ever Green */
  everGreenStep1: everGreenStep1Reducer,
  everGreenStep2: everGreenStep2Reducer,
  everGreenStep3: everGreenStep3Reducer,
  everGreenContact: everGreenContactReducer,
  everGreenStep4: everGreenStep4Reducer,

  /** Cancer Cover */
  cancerCoverStep1: cancerCoverStep1Reducer,
  cancerCoverStep2: cancerCoverStep2Reducer,
  cancerCoverStep3: cancerCoverStep3Reducer,
  cancerCoverStep4: cancerCoverStep4Reducer,
  cancerCoverStep6: cancerCoverStep6Reducer,
  cancerCoverContact: cancerCoverContact,
});

const persistConfig = {
  key: "root",
  storage: storageSession,
  blacklist: ["navigation"],
};

const getExpiredTime = () => {
  const now = new Date().getTime();
  const duration = get(
    window,
    "AVIVA_VARIABLE_CONFIG.persistDataTimeout",
    60000 * 25
  );
  return now + duration;
};

const checkExpired = () => {
  const now = new Date().getTime();
  const clearCached = Helpers.getURLSearchParams().get("clear_cached");
  const persisExpiredTime = sessionStorage.getItem("persist:expired") || 0;
  if (clearCached || !persisExpiredTime || now > Number(persisExpiredTime)) {
    Helpers.clearCached();
    const time = getExpiredTime();
    sessionStorage.setItem("persist:expired", `${time}`);
  }
};

const refresher =
  () =>
  ({ getState }: any) =>
  (next: any) =>
  (action: any) => {
    const time = getExpiredTime();
    sessionStorage.setItem("persist:expired", `${time}`);
    if (action.type === "persist/REHYDRATE" && action.payload) {
      action.payload.loading.isShowing = false;
    }

    let returnedValue = next(action);
    return returnedValue;
  };

checkExpired();

const persistedReducer = persistReducer(persistConfig, reducers);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(refresher()),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
